import { createReducer, on } from '@ngrx/store';

import { INITIAL_SURVEY_CREATION_AUDIENCE_STATE } from '../survey-creation-state.constants';
import { SurveyCreationAudienceState } from '../survey-creation-state.model';
import {
  SurveyCreationActions,
  SurveyCreationApiActions,
  SurveyCreationNavbarApiActions,
} from './survey-creation-audience.actions';

export const surveyCreationAudienceReducer = createReducer(
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,

  on(
    SurveyCreationActions.init,
    (state, { surveyId }): SurveyCreationAudienceState => {
      // Already hydrated for this exact survey (e.g. just transitioned from
      // create → edit) — keep the in-memory survey instead of wiping it and
      // forcing loadSurveyForEdit$ to re-fetch what we already have.
      const alreadyHydrated =
        !!surveyId && state.surveyId === surveyId && !!state.survey;

      return alreadyHydrated
        ? state
        : { ...INITIAL_SURVEY_CREATION_AUDIENCE_STATE, surveyId };
    },
  ),

  on(
    SurveyCreationApiActions.loadSurveyForEditSuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationActions.updateMetadata,
    (state, { metadata }): SurveyCreationAudienceState => ({
      ...state,
      metadata,
    }),
  ),

  on(
    SurveyCreationActions.updateQualifications,
    (state, { qualifications }): SurveyCreationAudienceState => ({
      ...state,
      qualifications,
    }),
  ),

  on(
    SurveyCreationActions.updateQuotas,
    (state, { quotas }): SurveyCreationAudienceState => ({
      ...state,
      quotas,
    }),
  ),

  on(
    SurveyCreationApiActions.createStandardSurveySuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      surveyId: survey.id,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationApiActions.createStandardSurveyFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationApiActions.saveSurveySuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      surveyId: survey.id,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationApiActions.saveSurveyFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationApiActions.loadSurveyForEditFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.computeScorePending,
    (state): SurveyCreationAudienceState => ({
      ...state,
      score: {
        ...state.score,
        loading: true,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.computeScoreSuccess,
    (state, { scorePercentage }): SurveyCreationAudienceState => ({
      ...state,
      score: {
        scorePercentage,
        loading: false,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.computeScoreFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      score: {
        scorePercentage: null,
        loading: false,
        error,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.recalculateFeasibilityPending,
    (state): SurveyCreationAudienceState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        loading: true,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.recalculateFeasibilitySuccess,
    (
      state,
      { total, quotaGroups, audience, hasQuota },
    ): SurveyCreationAudienceState => ({
      ...state,
      feasibility: {
        total,
        quotaGroups,
        audience,
        hasQuota,
        loading: false,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationNavbarApiActions.recalculateFeasibilityFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        loading: false,
        error,
      },
    }),
  ),

  on(
    SurveyCreationActions.reset,
    (): SurveyCreationAudienceState => INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
  ),
);
